import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { CSVLink } from "react-csv";
import currency from "currency.js";
import moment from "moment";
import { Dropdown, Form } from "react-bootstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { URLS } from "../urls";
import { Row } from "reactstrap";
import Loader from "../Loader";
import {
  allMonths,
  allBanks,
  getQueryParams,
  fetchActionsUtil,
} from "../../utils";
import { ExcelIcon, ExportIcon } from "../icons";
import { useDownloadExcel } from "../useDownloadExcel";
import SentToAccount from "./SendToAccount";
import TableComponent from "../TableComponent";
import createWorker from "./salaryWorker";
import NoTableItem from "../NoTableItem";
import { toast } from "react-toastify";

export function SalaryComponent({
  TopBar,
  SideBar,
  branch_id,
  SwitchCompanyComponent,
}) {
  const query = getQueryParams();
  const date = new Date();
  const [payrollDate, setPayrollDate] = useState({
    payrollMonth: query?.month ? parseInt(query?.month) : date.getMonth() + 1,
    payrollYear: date.getFullYear(),
  });

  const [isLoading, setIsLoading] = useState(false);

  const [payroll, setPayroll] = useState([]);
  const [totalNetpay, setTotalNetPay] = useState(0);
  const [btnSummryActive, setBtnSummaryActive] = useState(false);
  const [salaryDetails, setSalaryDetails] = useState([]);
  const [bank, setBank] = useState("");
  const [employeeType, setEmployeeType] = useState("All");
  const [deductionItems, setDeductionItems] = useState([]);
  const [show, onShow] = useState(false);
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  const [salaryType, setSalaryType] = useState("");

  const [excelData, setExcelData] = useState([]);

  const CSVLinkRef = useRef(null);

  // fetch excel hook
  const [isfetchingExcel] = useDownloadExcel(excelData, CSVLinkRef);

  // Worker reference to manage the web worker
  // This will allow us to create a web worker for processing payroll data
  const workerRef = useRef(null);

  // Caching payroll data to avoid unnecessary API call
  // This will store the payroll data in memory to avoid fetching the same data multiple times
  // It uses a ref to store the cache, which won't cause re-renders when updated
  // This allows us to keep the cache across renders without triggering re-renders
  // This is useful for performance optimization, especially when dealing with large datasets
  const payrollCache = useRef({});

  // Worker cleanup on unmount
  useEffect(() => {
    const currentWorker = workerRef.current;
    return () => {
      if (currentWorker) {
        currentWorker.terminate();
      }
    };
  }, []);

  // Fetch job categories on component mount
  const fetchCategory = useCallback(async () => {
    try {
      const categoryData = await fetchActionsUtil(
        `${URLS.backendJobCategory}/get/${branch_id}`,
        "GET"
      );
      setCategories(categoryData?.jobCategories || []);
    } catch (error) {
      console.error("Error fetching job categories:", error);
    }
  }, [branch_id]);

  useEffect(() => {
    let isMounted = true;
    // Fetch job categories when the component mounts
    if (isMounted) {
      fetchCategory();
    }
    // Cleanup function to avoid memory leaks
    return () => {
      setCategories([]);
      isMounted = false;
    };
  }, [fetchCategory]);

  const params = useMemo(() => {
    return new URLSearchParams({
      payrollMonth: payrollDate.payrollMonth,
      payrollYear: payrollDate.payrollYear,
      employeeType,
      category,
      fullAndFinally: salaryType,
    });
  }, [payrollDate, employeeType, category, salaryType]);

  // Function to fetch payroll data
  const getPayroll = useCallback(async () => {
    try {
      // Check if payroll data is already cached
      const month = params.get("payrollMonth");
      const year = params.get("payrollYear");
      const cacheKey = `${year}-${month}`;
      if (payrollCache.current[cacheKey]) {
        processOnMainThread(payrollCache.current[cacheKey]);
        return;
      }
      // If not cached, fetch from API
      setIsLoading(true);

      const response = await fetchActionsUtil(
        `${URLS.backendPayroll}/get-payroll/${branch_id}?${params}`
      );

      const { payroll } = response;

      if (!payroll || payroll.length === 0) {
        setPayroll([]);
        setTotalNetPay(0);
        setDeductionItems([]);
        return;
      }

      // Process small datasets on main thread (<1000 records)
      if (payroll.length <= 1000) {
        processOnMainThread(payroll);
        return;
      }

      // Use Web Worker for large datasets (>1000 records)
      if (!workerRef.current) {
        workerRef.current = createWorker();
      }

      // Set up worker message handler
      workerRef.current.onmessage = (e) => {
        const { totalAmount, deductions } = e.data;
        setTotalNetPay(totalAmount);
        setDeductionItems(deductions);
        setPayroll(payroll);
        setIsLoading(false);
      };

      // Handle worker errors
      workerRef.current.onerror = (error) => {
        console.error("Web Worker error:", error);
        setIsLoading(false);
        // Fall back to main thread processing
        processOnMainThread(payroll);
      };

      // Send data to worker
      workerRef.current.postMessage(payroll);

      // Cache results
      payrollCache.current[cacheKey] = payroll;
    } catch (err) {
      console.error("Payroll error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [params, branch_id]);

  // Main thread processing for smaller datasets
  function processOnMainThread(payroll) {
    let totalAmount = 0;
    const deductionMap = new Map();

    // Efficient processing using for loops
    for (let i = 0; i < payroll.length; i++) {
      const employee = payroll[i];

      // Accumulate net pay
      totalAmount += employee.netPay || 0;

      const deductions = employee.deductions || [];
      for (let j = 0; j < deductions.length; j++) {
        const d = deductions[j];
        const key = d.groupName || d.name;
        if (!key) continue;

        deductionMap.set(key, (deductionMap.get(key) || 0) + (d.total || 0));
      }
    }

    const deductions = [];
    deductionMap.forEach((amount, name) => {
      deductions.push({ name, amount });
    });

    setTotalNetPay(totalAmount);
    setPayroll(payroll);
    setDeductionItems(deductions);
  }

  // Function to fetch salary details
  const getSalaryDetails = useCallback(async () => {
    try {
      const response = await fetchActionsUtil(
        `${URLS.backendSalary}/single/${branch_id}?${params}`
      );
      setSalaryDetails(response.salary);
    } catch (error) {
      console.error("Error fetching salary details:", error);
    }
  }, [branch_id, params]);

  // useEffect to fetch payroll and salary details when the component mounts or when dependencies change
  useEffect(() => {
    // Fetch payroll and salary details when the component mounts or when dependencies change
    let isMounted = true;
    if (isMounted) {
      getPayroll();
      getSalaryDetails();
    }
    return () => {
      // Cleanup function to avoid memory leaks
      isMounted = false;
    };
  }, [getPayroll, getSalaryDetails]);

  const onDownloadExcelData = async () => {
    if (!payroll?.length > 0) return toast.error("Nothing to download!!!");
    let normalExData = payroll.map((employee, index) => [
      index + 1,
      employee.netPay,
      `=""${employee.debitAccountNo}""`,
      `=""${employee.debitBankCode}"`,
      bank,
      `=""${employee.creditAccountNo}""`,
      `=""${employee.creditBankCode}""`,
      `=""${employee?.user?.bankDetails?.bankName}""`,
      `=""${employee?.employeeId}""`,
      employee.user?.personalInfo?.name.first +
        " " +
        employee.user?.personalInfo?.name.last,
      new Date(employee.createdAt).toDateString(),
      `${allMonths[employee.payrollMonth - 1]} ${employee.payrollYear} Salary`,
    ]);

    const firstBankExData = payroll.map((employee, index) => [
      `=""${employee.debitAccountNo}""`,
      `=""${employee.creditAccountNo}""`,
      `=""${employee.creditBankCode}""`,
      `${employee.user?.personalInfo?.name.first || ""} ${
        employee.user?.personalInfo?.name?.middle || ""
      } ${employee.user?.personalInfo?.name.last || ""}`,
      `${allMonths[employee.payrollMonth - 1]} ${employee.payrollYear} Salary`,
      employee.netPay,
    ]);

    let normalHead = [
      "No",
      "Net Amount",
      "Debit Account No",
      "Debit Bank Code",
      "Debit Bank",
      "Credit Account No",
      "Credit Bank Code",
      "Credit Bank",
      "Employee Id",
      "Beneficiary",
      "Payment Date",
      "Narration",
    ];

    const FirstBankHead = [
      "DebitAccountNo",
      "CreditAccountNo",
      "CreditBankCode",
      "BeneficiaryName",
      "Narration",
      "Amount",
    ];

    if (bank === "First Bank") {
      normalExData = firstBankExData;
      normalHead = FirstBankHead;
    }

    const date = `Date Prepared: ${moment(new Date()).format(
      "MMMM DD yyyy k:mm:ss z"
    )}`;

    const exData = [
      // [company],
      ["Payment History Report"],
      [date],
      [""],
      normalHead,
      ...normalExData,
      [""],
      ["Salary Summary"],
      [
        "Total Net Amount",
        currency(totalNetpay, {
          symbol: "",
        }).format(),
      ],
    ];

    // console.log(exData);
    setExcelData(exData);
  };

  const tableHead = () => {
    return (
      <thead>
        <tr>
          <th>#</th>
          <th>Net Amount</th>
          <th>Debit Account No</th>
          <th>Debit Bank Code</th>
          <th>Debit Bank</th>
          <th>Credit Account No</th>
          <th>Credit Bank Code</th>
          <th>Credit Bank</th>
          <th>Employee Id</th>
          <th>Beneficiary</th>
          <th>Payment Date</th>
          <th>Narration</th>
        </tr>
      </thead>
    );
  };

  const FirstBankTableHead = () => {
    return (
      <thead>
        <tr>
          <th>Debit Account No</th>
          <th>Credit Account No</th>
          <th>Credit Bank Code</th>
          <th>Beneficiary</th>
          <th>Narration</th>
          <th>Amount</th>
        </tr>
      </thead>
    );
  };

  const FirstBankTableBodyData = (employee, index) => {
    return (
      <>
        <td>{employee.debitAccountNo}</td>
        <td>{employee.creditAccountNo}</td>
        <td>{employee.creditBankCode}</td>
        <td>
          {`${employee.user?.personalInfo?.name.first || ""} ${
            employee.user?.personalInfo?.name?.middle || ""
          } ${employee.user?.personalInfo?.name.last || ""}`}
        </td>
        <td>
          {`${allMonths[employee.payrollMonth - 1]} 
                                   
                                    ${employee.payrollYear} `}
          Salary
        </td>
        <td>
          {currency(employee.netPay, {
            symbol: "",
          }).format()}
        </td>
      </>
    );
  };

  const tableBodyData = (employee, index) => {
    return (
      <>
        <td>{index + 1}</td>
        <td>
          {currency(employee.netPay, {
            symbol: "",
          }).format()}
        </td>
        <td>{employee.debitAccountNo}</td>
        <td>{employee.debitBankCode}</td>
        <td>{bank}</td>
        <td>{employee.creditAccountNo}</td>
        <td>{employee.creditBankCode}</td>
        <td>{employee?.user?.bankDetails?.bankName}</td>
        <td>{employee?.employeeId}</td>
        <td>
          {`${employee.user?.personalInfo?.name.first || ""} ${
            employee.user?.personalInfo?.name?.middle || ""
          } ${employee.user?.personalInfo?.name.last || ""}`}
        </td>
        <td>{new Date(employee.createdAt).toDateString()}</td>
        <td>
          {`${allMonths[employee.payrollMonth - 1]} ${employee.payrollYear} `}{" "}
          Salary
        </td>
      </>
    );
  };

  return (
    <body>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="robots" content="noindex, nofollow"></meta>
        <title>InvexERP</title>
      </Helmet>
      <div className="main-wrapper">
        <TopBar />
        <SideBar />
        {/*<!-- Page Wrapper -->*/}
        <div className="page-wrapper">
          {/*<!-- Page Content -->*/}
          <div className="content container-fluid">
            {/*<!-- Page Title -->*/}
            <div className="row">
              <div className="col-sm-12 col-12 text-capitalize">
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Link to={`${URLS.dashboard}`}>Dashboard</Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem active>Salary Schedule</BreadcrumbItem>
                </Breadcrumb>
              </div>
              <div className="col-sm-5 col-5"></div>
              {/*<div className="col-sm-7 col-7 text-right m-b-30"></div>*/}
            </div>
            {/*<!-- /Page Title -->*/}
            {/*<!-- Content Starts -->*/}
            <div className="mt-3">
              <div className="d-flex justify-content-between my-4">
                <div className="d-flex">
                  <div className="form-inline">
                    <label>Month</label>
                    <select
                      className="form-control mx-2"
                      value={payrollDate.payrollMonth}
                      onChange={(e) => {
                        setPayrollDate({
                          payrollMonth: e.target.value,
                          payrollYear: payrollDate.payrollYear,
                        });
                      }}
                    >
                      {[...Array(12)].map((e, index) => (
                        <option key={index}>{index + 1}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-inline mx-2">
                    <label>Year</label>
                    <input
                      type="number"
                      style={{ width: "100px" }}
                      className="form-control mx-2"
                      value={payrollDate.payrollYear}
                      onChange={(e) => {
                        setPayrollDate({
                          payrollMonth: payrollDate.payrollMonth,
                          payrollYear: e.target.value,
                        });
                      }}
                    />
                  </div>
                  <div className="form-inline">
                    <select
                      className="form-control"
                      aria-label="Default select example"
                      onChange={(e) => setBank(e.target.value)}
                    >
                      {allBanks.map((el) => (
                        <option value={el.value}>{el.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mx-2">
                    <select
                      className="form-control"
                      aria-label="Default select example"
                      value={employeeType}
                      onChange={(e) => setEmployeeType(e.target.value)}
                    >
                      <option value="All">All Employee</option>
                      <option value="staff">Staff</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div className="mx-2">
                    <select
                      className="form-control"
                      aria-label="Default select example"
                      value={salaryType}
                      onChange={(e) => setSalaryType(e.target.value)}
                    >
                      <option value="">Normal Payroll</option>
                      <option value={true}>FullAndFinal</option>
                    </select>
                  </div>

                  <div className="mx-2">
                    <Form.Control
                      as="select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      name="category"
                    >
                      <option value="">All Categories</option>
                      {categories && categories.length
                        ? categories.map((category) => {
                            return (
                              <option value={category._id}>
                                {category.name}
                              </option>
                            );
                          })
                        : null}
                    </Form.Control>
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  {payroll.length > 0 && salaryDetails?.status && (
                    <>
                      {salaryDetails?.paymentsStatus === "Paid" ? (
                        <p className="px-3 py-2 mx-3 rounded fw-bold text-white bg-success">
                          {salaryDetails?.paymentsStatus}
                        </p>
                      ) : (
                        <p
                          className={`px-3 py-2 mx-3 rounded fw-bold text-white ${
                            salaryDetails?.status === "Disapproved"
                              ? "bg-danger"
                              : salaryDetails?.status === "Approved & Closed"
                              ? "bg-success"
                              : salaryDetails?.status === "Pending"
                              ? "bg-warning"
                              : "bg-secondary"
                          }`}
                        >
                          {salaryDetails?.status}
                        </p>
                      )}
                    </>
                  )}

                  {payroll.length > 0 && (
                    <>
                      {salaryDetails?.status !== "Approved & Closed" &&
                        salaryDetails?.status !== "Pending" && (
                          <p
                            className="px-3 py-2 mx-3 rounded fw-bold text-white bg-info p-cursor"
                            onClick={() => onShow(true)}
                          >
                            Request for approval
                          </p>
                        )}
                    </>
                  )}

                  <div className="mx-4">
                    <CSVLink
                      className="btn print d-none"
                      filename={`SalarySummary(${moment(new Date()).format(
                        "DD-MMM-yyyy hh:mm:ss a"
                      )}).csv`}
                      data={excelData}
                      ref={CSVLinkRef}
                    />

                    <Dropdown>
                      <Dropdown.Toggle
                        variant=""
                        className="print"
                        disabled={isfetchingExcel}
                        bsPrefix=""
                      >
                        <span className="mx-2">Export</span>
                        <ExportIcon color="#008000" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        popperConfig={{
                          strategy: "fixed",
                        }}
                        renderOnMount
                        className="text-center"
                      >
                        <Dropdown.Item
                          as="div"
                          onClick={onDownloadExcelData}
                          className="p-cursor"
                        >
                          Excel <ExcelIcon color="#008000" />
                        </Dropdown.Item>
                        {/* <Dropdown.Item as="div">
                          <a
                            href={`${backendUrl}/api/reports/pdf/sales-analysis?${queryString.stringify(
                              newQuery
                            )}`}
                            target="blank"
                          >
                            PDF
                            <PDFIcon color="#ff0000" />
                          </a>
                        </Dropdown.Item> */}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>

              {SwitchCompanyComponent ? (
                <div>
                  <SwitchCompanyComponent />
                </div>
              ) : null}

              {isLoading && <Loader />}
              {payroll.length ? (
                <Row className="m-0">
                  <div className="col-md-12 w-100">
                    <div className="table-responsive">
                      <TableComponent
                        responsive
                        striped
                        sticky
                        tableHeadsFunction={
                          bank === "First Bank" ? FirstBankTableHead : tableHead
                        }
                        mainDataArray={payroll}
                        tableDataRowFunction={
                          bank === "First Bank"
                            ? FirstBankTableBodyData
                            : tableBodyData
                        }
                        className="product-table text-nowrap"
                      />
                    </div>
                  </div>
                </Row>
              ) : (
                <NoTableItem
                  title={
                    payrollDate.payrollMonth
                      ? `No data on ${
                          allMonths[payrollDate.payrollMonth - 1]
                        } ${payrollDate.payrollYear}`
                      : "Please Select the month for payroll"
                  }
                />
              )}
            </div>

            <div
              className={
                btnSummryActive
                  ? `summaryNew border-top open`
                  : `summaryNew border-top`
              }
            >
              <div className="row justify-content-between mt-4">
                <div className="col">
                  <div className="my-3">
                    <div className="d-flex gap-3 align-items-center mb-4">
                      <h4 className="m-0">Total Net Amount</h4>{" "}
                      <hr className="flex-grow-1 m-0" />
                      {btnSummryActive && (
                        <button
                          onClick={() => setBtnSummaryActive(!btnSummryActive)}
                          className="btn p-0"
                        >
                          {/* <SummaryCloseIcon /> */}
                        </button>
                      )}
                    </div>

                    <div
                      className="gridCont mb-3 three-col-grid"
                      style={{ gridTemplateRows: "unset" }}
                    >
                      {/*  */}
                      <div className="gridChild gridChildBorderLeftBlue">
                        <div className="gridChildLeft">
                          <p className="gridChld1">
                            {currency(totalNetpay, {
                              symbol: "",
                            }).format()}
                          </p>
                          <p className="gridChld2">Total Net Amount</p>
                        </div>
                      </div>
                      {/*  */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/*<!-- /Content End -->*/}
          </div>
          {/*<!-- /Page Content -->*/}
        </div>
        {/*<!-- /Page Wrapper -->*/}

        {show && (
          <SentToAccount
            payrollDate={payrollDate}
            Amount={currency(totalNetpay, {
              symbol: "",
            }).format()}
            employeeType={employeeType}
            deductionItems={deductionItems}
            show={show}
            onShow={() => onShow(false)}
            category={category}
            salaryType={salaryType}
          />
        )}
      </div>
      {/*<!-- /Page Wrapper -->*/}
      {/*<!-- /Main Wrapper -->*/} {/* <!-- Sidebar Overlay --> */}
      <div className="sidebar-overlay" data-reff=""></div>
    </body>
  );
}
